import type { ScanOutcomeCode } from './scan-agent';

/** What a person reads when a scan ends badly: what happened, and what to do, in order. */
export interface ScanOutcomeWords {
  title: string;
  steps: string[];
}

function kept(pages: number): string {
  if (pages === 0) return '';
  return pages === 1 ? ' The first page is kept.' : ` The ${pages} pages before it are kept.`;
}

type Words = (pages: number) => ScanOutcomeWords;

const startAgain =
  (title: string): Words =>
  () => ({
    title,
    steps: ['Check that paper is loaded and the scanner shows no message.', 'Scan again.'],
  });

const couldNotFinish: Words = (pages) => ({
  title: `The scanner could not finish.${kept(pages)}`,
  steps: ['Scan again.', 'If it happens again, switch the scanner off and on, then scan again.'],
});

const words: Partial<Record<ScanOutcomeCode, Words>> = {
  noPaper: () => ({
    title: 'The feeder is empty',
    steps: ['Put the pages in the feeder, as the scanner shows.', 'Scan again.'],
  }),
  paperJam: (pages) => ({
    title: `The paper jammed.${kept(pages)}`,
    steps: [
      'Open the scanner and clear the jam.',
      'Put the pages that did not go through back in the feeder.',
      'Choose Scan more.',
    ],
  }),
  doubleFeed: (pages) => ({
    title: `Two sheets went through together.${kept(pages)}`,
    steps: [
      'Take out the pages that did not go through and fan them.',
      'Put them back in the feeder.',
      'Choose Scan more.',
    ],
  }),
  coverOpen: () => ({
    title: 'The scanner’s cover or feeder is open',
    steps: ['Close it until it clicks.', 'Scan again.'],
  }),
  offline: (pages) => ({
    title: `The scanner stopped answering.${kept(pages)}`,
    steps: [
      'Check that it is switched on and its cable is plugged in.',
      'Scan again; if it is not listed, choose Try again.',
    ],
  }),
  busy: () => ({
    title: 'Another program is using the scanner',
    steps: ['Close the other scanning program.', 'Scan again.'],
  }),
  notFound: () => ({
    title: 'The scanner is no longer connected',
    steps: ['Check its cable and power.', 'Choose it again from the list.'],
  }),
  noManager: () => ({
    title: 'The scanner’s software is not fully installed on this computer',
    steps: ['Reinstall the scanner’s software from its maker’s website.', 'Then try again.'],
  }),
  stoppedByScanner: startAgain('The scanner stopped before scanning'),
  timedOut: startAgain('The scanner did not start'),
  driverCrashed: couldNotFinish,
  driverHung: couldNotFinish,
  failed: couldNotFinish,
};

/**
 * Paige's words for each way a scan can end badly (Paige Scan 1.5.2's outcome codes). Pages already
 * scanned are always kept, and the words say so, so nobody scans them twice. Null for a scan that
 * completed or was stopped, and for a code this page does not know (an older or newer agent): the
 * caller then shows the agent's own sentence.
 */
export function wordsForOutcome(
  outcome: ScanOutcomeCode | undefined,
  pages: number,
): ScanOutcomeWords | null {
  const say = outcome ? words[outcome] : undefined;
  return say ? say(pages) : null;
}

/** The line under the pages after a scan that went well: how many came, and the blank sides left out. */
export function scanSummary(pages: number, blank: number): string | null {
  if (blank === 0) return null;
  const sides = blank === 1 ? '1 blank side was' : `${blank} blank sides were`;
  return pages === 0
    ? `Every side was blank, so nothing was added (${sides} left out).`
    : `${sides[0]!.toUpperCase()}${sides.slice(1)} left out.`;
}
